import React from 'react';
import { useMeetingAppContext } from '../../../MeetingAppContextDef';

/**
 * BrandingOverlays - Renders QR code overlay on the meeting screen
 * Logo is now rendered inside ParticipantView for the local participant
 * QR code is shown at screen level as it's meant to be visible to viewers
 */
export default function BrandingOverlays() {
  const { branding } = useMeetingAppContext();

  if (!branding) return null;

  const {
    // QR Code settings
    showQrCode,
    qrUrl,
    qrPosition,
    qrColor,
    qrBgColor,
    qrSize,
    qrLogo,
  } = branding;

  // Position mapping for QR code
  const qrPositionStyles = {
    'top-left': { top: '16px', left: '16px' },
    'top-center': { top: '16px', left: '50%', transform: 'translateX(-50%)' },
    'top-right': { top: '16px', right: '16px' },
    'middle-left': { top: '50%', left: '16px', transform: 'translateY(-50%)' },
    'middle-right': { top: '50%', right: '16px', transform: 'translateY(-50%)' },
    'bottom-left': { bottom: '80px', left: '16px' },
    'bottom-center': { bottom: '80px', left: '50%', transform: 'translateX(-50%)' },
    'bottom-right': { bottom: '80px', right: '16px' },
  };

  // QR size mapping
  const qrSizeStyles = {
    small: { width: '80px', height: '80px' },
    medium: { width: '120px', height: '120px' },
    large: { width: '160px', height: '160px' },
  };

  // Only render if QR code is enabled
  if (!showQrCode || !qrUrl) return null;

  return (
    <>
      {/* QR Code Overlay */}
      {showQrCode && qrUrl && (
        <div
          className="absolute z-30 pointer-events-none"
          style={{
            ...qrPositionStyles[qrPosition || 'bottom-right'],
          }}
        >
          <div
            className="rounded-xl p-2 shadow-lg relative"
            style={{
              backgroundColor: qrBgColor || '#ffffff',
              ...qrSizeStyles[qrSize || 'medium'],
            }}
          >
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=${(qrColor || '#001233').replace('#', '')}&bgcolor=${(qrBgColor || '#ffffff').replace('#', '')}&data=${encodeURIComponent(qrUrl)}`}
              alt="QR Code"
              className="w-full h-full object-contain"
            />
            {/* Center logo on QR if provided */}
            {qrLogo && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: qrBgColor || '#ffffff' }}
                >
                  <img
                    src={qrLogo}
                    alt="QR Logo"
                    className="w-5 h-5 md:w-6 md:h-6 object-contain rounded-full"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * VideoFrameOverlay - Renders decorative frame around participant video
 */
export function VideoFrameOverlay({ children, isActiveSpeaker, isSpotlighted }) {
  const { branding } = useMeetingAppContext();

  // Active speaker / spotlight: solid border, no glow/animation
  if (isActiveSpeaker || isSpotlighted) {
    const borderColor = isSpotlighted ? 'rgba(168, 85, 247, 1)' : 'rgba(34, 197, 94, 1)';
    return (
      // `rounded-xl overflow-hidden` on the wrapper makes the inner content
      // clip at the same radius the border overlay uses (0.75rem). Without
      // this the parent's background bleeds through at each rounded corner,
      // producing the "white corners" artifact users see on non-speaker
      // tiles whose parent grid has a lighter background.
      <div className="relative w-full h-full rounded-xl overflow-hidden">
        {children}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 5,
            borderRadius: '0.75rem',
            border: `3px solid ${borderColor}`,
          }}
        />
      </div>
    );
  }

  if (!branding || branding.frame === 'none') {
    // Even without a branding frame, wrap in a clipped container so the
    // inner video doesn't expose the parent grid background through its
    // rounded corners.
    return <div className="relative w-full h-full rounded-xl overflow-hidden">{children}</div>;
  }

  const {
    frame,
    frameColor,
    frameBorderWidth,
    frameShadow,
    gradientColor1,
    gradientColor2,
  } = branding;

  const getFrameStyle = () => {
    const baseStyle = {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 5,
    };

    // Use `outline` instead of `border` for the solid frame styles.  Reason:
    // the overlay div sits at `inset: 0` inside a wrapper with
    // `overflow-hidden` + `rounded-xl` (12px) whose radius is smaller than
    // the overlay's own 16px.  A `border` is rendered inside the overlay's
    // own box, so its corners extend beyond the wrapper's rounded clip and
    // get silently eaten — especially on desktop at DPR=1 where 1–2px
    // differences disappear entirely.  `outline` is drawn outside the box
    // model and follows the overlay's own `border-radius`, so the width is
    // always honoured consistently across desktop + mobile.
    switch (frame) {
      case 'rounded':
        return {
          ...baseStyle,
          outline: `${frameBorderWidth || 2}px solid ${frameColor || '#6a1fbf'}`,
          outlineOffset: `-${frameBorderWidth || 2}px`,
          borderRadius: '16px',
          boxShadow: frameShadow ? `0 0 20px ${frameColor || '#6a1fbf'}40` : 'none',
        };
      case 'circle':
        return {
          ...baseStyle,
          outline: `${frameBorderWidth || 2}px solid ${frameColor || '#6a1fbf'}`,
          outlineOffset: `-${frameBorderWidth || 2}px`,
          borderRadius: '50%',
          boxShadow: frameShadow ? `0 0 20px ${frameColor || '#6a1fbf'}40` : 'none',
        };
      case 'gradient':
        return {
          ...baseStyle,
          border: `${frameBorderWidth || 2}px solid transparent`,
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${gradientColor1 || '#6a1fbf'}, ${gradientColor2 || '#00c853'}) border-box`,
          WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          boxShadow: frameShadow
            ? `0 0 20px ${gradientColor1 || '#6a1fbf'}40, 0 0 40px ${gradientColor2 || '#00c853'}20`
            : 'none',
        };
      default:
        return baseStyle;
    }
  };

  // For circle, the wrapper itself must be a square clipped to 50% — otherwise
  // the video remains rectangular and only the border overlay looks circular
  // (the "rectangular frame with a circular mask" artifact).  We centre a
  // square inside the grid cell using aspect-ratio:1, constrained by the
  // cell's shorter dimension so the circle always fits.
  if (frame === 'circle') {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <div
          className="relative overflow-hidden"
          style={{
            aspectRatio: '1 / 1',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            borderRadius: '50%',
          }}
        >
          {children}
          <div style={getFrameStyle()} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden">
      {children}
      <div style={getFrameStyle()} />
    </div>
  );
}
